import {
  AssessmentType,
  SurveyCreateDto,
  SurveyListSelectDto,
  SurveyProductionRateDto,
  SurveySelectDto,
  SurveyStatus,
  SurveysSelectDto,
} from '@@types/api.types.ts';
import { ApiErrorWrapper, useApi } from '@hooks/api.hook.tsx';
import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from './client.ts';
import { queryKeys } from './keys.ts';
import { UserRoleEndpoint, useGetCurrentUser } from './users.query.ts';

const surveyRoleEndpoints: UserRoleEndpoint[] = ['innovator', 'expert', 'intel'];

export const useInnovatorCompleteSurvey = () => {
  const { apiPost } = useApi();
  const { isInnovator } = useGetCurrentUser();

  return useMutation<SurveySelectDto, ApiErrorWrapper, { surveyId: string }>({
    mutationFn: async (formData) => {
      if (!isInnovator)
        throw new ApiErrorWrapper({ errors: [{ message: 'Only Innovators can complete surveys', statusCode: 403 }] });
      return await apiPost<SurveySelectDto>({
        path: `/v1/innovator/surveys/${formData.surveyId}/complete`,
      });
    },
    onSuccess: (data: SurveySelectDto) => {
      const technologyId = data.technology.id;

      // Invalidate survey queries
      queryClient.invalidateQueries({ queryKey: queryKeys.survey.getSurvey(data.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.survey.getSurveys(technologyId) });

      // Invalidate technology queries
      queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologies });
      queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnology(technologyId) });

      // Invalidate assessment queries - critical for TRL/MRL tabs to show new data
      queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologyTrlAssessment(technologyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologyMrlAssessment(technologyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.technology.getAssessmentHistory(technologyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologyUsesWithAssessments(technologyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologyTrlAnalysis(technologyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologyTrlProjection(technologyId) });

      // Invalidate general assessments list
      queryClient.invalidateQueries({
        queryKey: queryKeys.assessments.getAssessments([AssessmentType.TRL], technologyId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.assessments.getAssessments([AssessmentType.MRL], technologyId),
      });
    },
  });
};

export const useInnovatorCreateSurvey = () => {
  const { isInnovator } = useGetCurrentUser();

  const onError = (error: ApiErrorWrapper) => {
    console.error('Error submitting answer:', error);
  };

  const onSuccess = (data: SurveySelectDto) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.survey.getSurveys(data.technology.id) });
  };

  const { apiPost } = useApi();

  return useMutation<SurveySelectDto, ApiErrorWrapper, SurveyCreateDto>({
    mutationFn: async (formData) => {
      if (!isInnovator) {
        throw new ApiErrorWrapper({ errors: [{ message: 'Only Innovators can create surveys', statusCode: 403 }] });
      }

      return await apiPost<SurveySelectDto>({
        path: '/v1/innovator/surveys',
        body: formData,
      });
    },
    onSuccess,
    onError,
  });
};

export const useInnovatorUpdateSurveyProductionRate = () => {
  const { apiPatch } = useApi();
  const { isInnovator } = useGetCurrentUser();

  return useMutation<SurveySelectDto, ApiErrorWrapper, { productionRate: SurveyProductionRateDto; surveyId: string }>({
    mutationFn: async (formData) => {
      if (!isInnovator) {
        throw new ApiErrorWrapper({ errors: [{ message: 'Only Innovators can update surveys', statusCode: 403 }] });
      }

      return await apiPatch<SurveySelectDto>({
        body: formData.productionRate,
        path: `/v1/innovator/surveys/${formData.surveyId}/production-rate`,
      });
    },
    onSuccess: (data: SurveySelectDto) => {
      const technologyId = data.technology.id;
      queryClient.invalidateQueries({ queryKey: queryKeys.survey.getSurvey(data.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.survey.getSurveys(technologyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.technology.getTechnologyMrlAssessment(technologyId) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.assessments.getAssessments([AssessmentType.MRL], technologyId),
      });
    },
  });
};

export const useGetSurvey = (surveyId?: string | null) => {
  const { roleEndpoint } = useGetCurrentUser();
  const hasAccess = roleEndpoint && surveyRoleEndpoints.includes(roleEndpoint);
  const enabled = Boolean(hasAccess && surveyId);

  const { apiGet } = useApi();

  return useQuery<SurveySelectDto>({
    enabled,
    queryFn: async () => {
      return await apiGet<SurveySelectDto>({
        path: `/v1/${roleEndpoint}/surveys/${surveyId}`,
      });
    },
    queryKey: enabled && surveyId ? queryKeys.survey.getSurvey(surveyId) : queryKeys.placeholders.default,
  });
};

export const useInnovatorDeleteSurvey = () => {
  const { apiDelete } = useApi();
  const { isInnovator } = useGetCurrentUser();

  const onSuccess = (data: SurveySelectDto) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.survey.getSurveys() });
    queryClient.removeQueries({ queryKey: queryKeys.survey.getSurvey(data.id) });
  };

  return useMutation<SurveySelectDto, ApiErrorWrapper, { surveyId: string }>({
    mutationFn: async ({ surveyId }) => {
      if (!isInnovator)
        throw new ApiErrorWrapper({ errors: [{ message: 'Only Innovators can delete surveys', statusCode: 403 }] });
      return await apiDelete({
        path: `/v1/innovator/surveys/${surveyId}`,
      });
    },
    onSuccess,
  });
};

type GetSurveysQuery = {
  status?: SurveyStatus;
  technologyId?: string;
};

export const useGetSurveysQueryOptions = (query: GetSurveysQuery) => {
  const { roleEndpoint } = useGetCurrentUser();
  const hasAccess = roleEndpoint && surveyRoleEndpoints.includes(roleEndpoint);
  const enabled = Boolean(hasAccess);

  const { apiGet } = useApi();

  return queryOptions<SurveyListSelectDto[], ApiErrorWrapper>({
    enabled,
    queryKey: enabled ? queryKeys.survey.getSurveys(query.technologyId, query.status) : queryKeys.placeholders.default,
    queryFn: async () => {
      const result = await apiGet<SurveysSelectDto>({
        path: `/v1/${roleEndpoint}/surveys`,
        query,
      });
      return result.surveys;
    },
  });
};

export const useGetSurveys = (query: GetSurveysQuery) => {
  const surveysQueryOptions = useGetSurveysQueryOptions(query);
  return useQuery(surveysQueryOptions);
};

export const useInnovatorGetSurveys = (query: GetSurveysQuery) => {
  const surveysQueryOptions = useGetSurveysQueryOptions(query);
  const { isInnovator, selectedCompany } = useGetCurrentUser();

  return useQuery({ ...surveysQueryOptions, enabled: surveysQueryOptions.enabled && isInnovator && !!selectedCompany });
};

export const useGetTechnologySurveys = (query: GetSurveysQuery) => {
  const surveysQueryOptions = useGetSurveysQueryOptions({ ...query, technologyId: query.technologyId ?? undefined });
  return useQuery({ ...surveysQueryOptions, enabled: surveysQueryOptions.enabled && !!query.technologyId });
};
